/**
 * Receiving helpers — shared queries for receiving history.
 * Used on PODetail (history below line items) and ReceivingDetail (history with
 * current record highlighted) and DiscrepancyDetail (PO-scoped history).
 *
 * Day 51 Phase 0 (B33): switched from PostgREST embed to server-side RPC.
 * The original embed `profiles:received_by(full_name)` doesn't work because
 * receiving_records.received_by FKs to auth.users(id), not profiles(id), and
 * PostgREST cannot traverse via auth.users (not exposed in the API). The
 * `get_receiving_history` RPC does the JOIN at SQL level using the shared
 * UUID space (profiles.id → auth.users.id, received_by → auth.users.id).
 */

import { supabase } from './supabase';

export interface ReceivingHistoryRow {
  id: string;
  received_at: string | null;
  status: string;
  received_by: string | null;
  received_by_name: string | null;
  line_item_count: number;
}

interface RawRpcRow {
  id: string;
  received_at: string | null;
  status: string;
  received_by: string | null;
  received_by_name: string | null;
  // line_item_count arrives as a string from PostgREST (Postgres bigint
  // serialized to JSON-string to avoid precision loss). Cast in the map below.
  line_item_count: string | number;
}

export async function fetchReceivingHistory(poId: string): Promise<ReceivingHistoryRow[]> {
  const { data, error } = await supabase.rpc('get_receiving_history', { p_po_id: poId });
  if (error || !data) return [];
  return (data as unknown as RawRpcRow[]).map((r) => ({
    id: r.id,
    received_at: r.received_at,
    status: r.status,
    received_by: r.received_by,
    received_by_name: r.received_by_name,
    line_item_count: Number(r.line_item_count) || 0,
  }));
}
